import React, { useState, useEffect, useMemo } from 'react';
import { ButtonGroup, Button, Form } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { stocktakingApi } from 'api';

echarts.use([
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  CanvasRenderer,
]);

const PERIOD_OPTIONS = [
  { label: '30D', value: 30 },
  { label: '60D', value: 60 },
  { label: '90D', value: 90 },
];

const CONTEXT_TYPE_OPTIONS = [
  { label: 'Reject (Not Found)', value: 'reject_not_found' },
  { label: 'Inventory Adjustment', value: 'inventory_adjustment' },
  { label: 'Stocktaking', value: 'stocktaking' },
];

const fillMissingDates = (data, days) => {
  const dateMap = {};
  data.forEach(item => {
    dateMap[item.date] = item.oos_quantity;
  });

  const result = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    result.push({
      date: dateStr,
      oos_quantity: dateMap[dateStr] || 0,
    });
  }
  return result;
};

const OOSDailyChart = () => {
  const [days, setDays] = useState(60);
  const [contextType, setContextType] = useState('reject_not_found');
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await stocktakingApi.getOosDaily({
          days,
          context_type: contextType,
        });
        if (!cancelled) setRawData(res || []);
      } catch {
        if (!cancelled) setRawData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [days, contextType]);

  const chartData = useMemo(
    () => fillMissingDates(rawData, days),
    [rawData, days],
  );

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: params => {
          const p = params[0];
          return `${p.axisValue}<br/>OOS: <strong>${p.value}</strong>`;
        },
      },
      grid: { left: 40, right: 12, top: 8, bottom: 30 },
      xAxis: {
        type: 'category',
        data: chartData.map(d => d.date),
        axisLabel: {
          rotate: 45,
          fontSize: 10,
          interval: Math.max(Math.floor(chartData.length / 10) - 1, 0),
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
      },
      series: [
        {
          type: 'bar',
          data: chartData.map(d => d.oos_quantity),
          itemStyle: { color: '#3874cb' },
        },
      ],
    }),
    [chartData],
  );

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small text-secondary">
            OOS Daily Trend
          </span>
          <Form.Select
            size="sm"
            value={contextType}
            onChange={e => setContextType(e.target.value)}
            style={{ width: 'auto' }}
          >
            {CONTEXT_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <ButtonGroup size="sm">
          {PERIOD_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              variant={days === opt.value ? 'primary' : 'outline-primary'}
              onClick={() => setDays(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </ButtonGroup>
      </div>
      {loading ? (
        <div className="text-center py-3 text-secondary small">Loading...</div>
      ) : (
        <ReactEChartsCore
          echarts={echarts}
          option={option}
          style={{ height: 120 }}
          notMerge
        />
      )}
    </div>
  );
};

export default OOSDailyChart;
